import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type SavedItemType =
  | "article"
  | "curated_post"
  | "idea"
  | "opportunity"
  | "event";

export interface SavedItemInput {
  item_type: SavedItemType;
  item_id: string;
  title: string;
  url?: string | null;
  source?: string | null;
  cover_image_url?: string | null;
  summary?: string | null;
  metadata?: Record<string, unknown>;
}

export interface SavedItem extends SavedItemInput {
  id: string;
  user_id: string;
  note: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
}

const QUERY_KEY = ["saved-items"] as const;

/** Quick lookup hook for one specific item (used by SaveButton). */
export const useIsSaved = (item_type: SavedItemType, item_id: string | null | undefined) => {
  const { items } = useSavedItems();
  return useMemo(
    () =>
      !!item_id &&
      items.some((i) => i.item_type === item_type && i.item_id === item_id),
    [items, item_type, item_id],
  );
};

export const useSavedItems = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: [...QUERY_KEY, user?.id ?? "anon"],
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async (): Promise<SavedItem[]> => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("saved_items")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SavedItem[];
    },
  });

  const items = query.data ?? [];

  const saveMutation = useMutation({
    mutationFn: async (input: SavedItemInput) => {
      if (!user) throw new Error("auth required");
      const payload = {
        user_id: user.id,
        item_type: input.item_type,
        item_id: input.item_id,
        title: input.title,
        url: input.url ?? null,
        source: input.source ?? null,
        cover_image_url: input.cover_image_url ?? null,
        summary: input.summary ?? null,
        metadata: (input.metadata ?? {}) as never,
      };
      const { error } = await supabase
        .from("saved_items")
        .upsert(payload, { onConflict: "user_id,item_type,item_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Saved to your profile");
    },
    onError: () => toast.error("Could not save"),
  });

  const removeMutation = useMutation({
    mutationFn: async ({ item_type, item_id }: { item_type: SavedItemType; item_id: string }) => {
      if (!user) throw new Error("auth required");
      const { error } = await supabase
        .from("saved_items")
        .delete()
        .eq("user_id", user.id)
        .eq("item_type", item_type)
        .eq("item_id", item_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => toast.error("Could not remove"),
  });

  const removeByIdMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error("auth required");
      const { error } = await supabase.from("saved_items").delete().eq("id", id).eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      if (!user) throw new Error("auth required");
      const { error } = await supabase
        .from("saved_items")
        .update({ note })
        .eq("id", id)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const toggle = useCallback(
    async (input: SavedItemInput) => {
      if (!user) {
        toast.message("Sign in to save", { description: "Bookmarks live on your profile." });
        navigate("/auth");
        return false;
      }
      const existing = items.find(
        (i) => i.item_type === input.item_type && i.item_id === input.item_id,
      );
      if (existing) {
        await removeMutation.mutateAsync({ item_type: input.item_type, item_id: input.item_id });
        return false;
      }
      await saveMutation.mutateAsync(input);
      return true;
    },
    [user, items, saveMutation, removeMutation, navigate],
  );

  return {
    items,
    loading: query.isLoading,
    isSavedKey: (type: SavedItemType, id: string) =>
      items.some((i) => i.item_type === type && i.item_id === id),
    save: (input: SavedItemInput) => saveMutation.mutateAsync(input),
    remove: (input: { item_type: SavedItemType; item_id: string }) =>
      removeMutation.mutateAsync(input),
    removeById: (id: string) => removeByIdMutation.mutateAsync(id),
    updateNote: (id: string, note: string) => updateNoteMutation.mutateAsync({ id, note }),
    toggle,
    refresh: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  };
};